<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Serial Number
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Blood Group
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Blood Product
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Patient Name
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  IP/OP Number
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Patient Blood Group
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Ward
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Requisition Date
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="flex items-center">
                  Issue Date
                </div>
              </th>
              <th scope="col" class="px-6 py-2">
                <div class="text-center">
                  Status
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(unit, index) in assigned_units" :key="index" class="border-b">
              <td class="px-6 py-3">{{ unit.product?.pint_serial }}</td>
              <td class="px-6 py-3">{{ unit.product?.blood_group?.description }}</td>
              <td class="px-6 py-3">{{ unit.product?.unit_product?.name }}</td>
              <td class="px-6 py-3">{{ unit.requisition_product?.patient_requisition?.patient?.first_name }}
                {{ unit.requisition_product?.patient_requisition?.patient?.last_name }}
              </td>
              <td class="px-6 py-3">{{ unit.requisition_product?.patient_requisition?.patient?.ipop }}</td>
              <td class="px-6 py-3">{{ unit.requisition_product?.patient_requisition?.patient?.blood_group?.description }}</td>
              <td class="px-6 py-3">{{ unit.requisition_product?.patient_requisition?.requisition?.department?.name }}</td>
              <td class="px-6 py-3">{{ formatDate(unit.requisition_product?.patient_requisition?.requisition?.date_added) }}</td>
              <td class="px-6 py-3">{{ formatDate(unit.issued_on) }}</td>
              <td class="px-6 py-3">
                <span class="p-2 rounded-lg" :class="{
                  'bg-green-100 text-green-800': ['Transfused', 'Issued'].includes(unit.product?.status),
                  'bg-red-100 text-red-800': ['Rejected'].includes(unit.product?.status)
                }">
                  {{ unit.product?.status === 'Issued' ? 'Received' : unit.product?.status }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex justify-center gap-4 mt-4">
        <button @click="previousPage" :disabled="currentPage === 1"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m15 19-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div class="flex items-center gap-2">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
            'bg-custom-blue text-white': page === currentPage,
            'text-custom-blue': page !== currentPage
          }"
            class="relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            type="button">
            <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              {{ page }}
            </span>
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          Next
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import api from "@/services/api";

export default {
  name: "IssuedUnitsReports",
  components: {},
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
    filters: {
      type: Object,
      default: () => ({
        blood_group: "",
        status: "",
        unit_product: "",
        start_date: "",
        end_date: ""
      })
    }
  },
  data() {
    return {
      currentPage: 1,
      totalPages: 0,
      perPage: 10,
      loading: false,
      assigned_units: [],
      isLoading: {},
      assignedUnitId: '',
      productId: '',
      transfusion: null,
      showClaimsModal: false,
      error: null,
      facility_logo: '',
      localSearchQuery: ''
    };
  },
  created() {
    this.getAssignedUnits();
    this.user = this.$store.getters["auth/user"];
    this.facility_logo = this.user.user_roles[0]?.facility_department?.facility?.logo;
    this.localSearchQuery = this.searchQuery;
  },
  computed: {
    visiblePages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      const range = [];
      const rangeWithDots = [];
      let l;

      for (let i = 1; i <= total; i++) {
        if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
          range.push(i);
        }
      }

      range.forEach(i => {
        if (l) {
          if (i - l === 2) {
            rangeWithDots.push(l + 1);
          } else if (i - l !== 1) {
            rangeWithDots.push('...');
          }
        }
        rangeWithDots.push(i);
        l = i;
      });

      return rangeWithDots;
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() {
        this.currentPage = 1;
        this.getAssignedUnits();
      }
    },
    searchQuery: {
      handler(newVal) {
        this.localSearchQuery = newVal;
        this.currentPage = 1;
        this.getAssignedUnits();
      },
      immediate: true
    },
    localSearchQuery: {
      handler(newVal) {
        this.$emit('update:searchQuery', newVal);
        this.currentPage = 1;
        this.getAssignedUnits();
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return 'N/A';
      const parsedDate = new Date(date);
      if (isNaN(parsedDate.getTime())) {
        console.error("Invalid date:", date);
        return 'N/A';
      }
      return parsedDate.toLocaleString();
    },

    applyFilters() {
      this.currentPage = 1;
      this.getAssignedUnits();
    },

    cancelFilters() {
      this.$emit('cancel-filters');
      this.currentPage = 1;
      this.getAssignedUnits();
    },

    handleLoadWitness(assigned_unit_id, product_id) {
      this.assignedUnitId = assigned_unit_id;
      this.productId = product_id;
      this.showClaimsModal = true;
    },

    closeClaimModal() {
      this.showClaimsModal = false;
    },

    async getAssignedUnits() {
      this.loading = true;
      try {
        const queryParams = {
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          unit_product: this.filters.unit_product,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search_query: this.localSearchQuery,
          page: this.currentPage,
          per_page: this.perPage
        };
        const response = await api.fetchAssignedUnitsReports(queryParams);
        this.assigned_units = response.data || [];
        this.currentPage = response.current_page || 1;
        this.totalPages = response.total_pages || 1;
      } catch (error) {
        this.error = error.message || "Failed to fetch assigned units";
        console.error("Error fetching assigned units:", error);
      } finally {
        this.loading = false;
      }
    },

    goToPage(page) {
      if (page !== '...' && page !== this.currentPage) {
        this.currentPage = page;
        this.getAssignedUnits();
      }
    },

    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.getAssignedUnits();
      }
    },

    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.getAssignedUnits();
      }
    },

    async handleDownloadIssuedUnits() {
      const printWindow = document.createElement('iframe');
      printWindow.style.position = 'absolute';
      printWindow.style.width = '0';
      printWindow.style.height = '0';
      document.body.appendChild(printWindow);

      try {
        const queryParams = {
          blood_group: this.filters.blood_group,
          status: this.filters.status,
          unit_product: this.filters.unit_product,
          start_date: this.filters.start_date,
          end_date: this.filters.end_date,
          search_query: this.localSearchQuery,
          all: true
        };

        const response = await api.fetchAssignedUnitsReports(queryParams);
        const issued_units = response.data || [];

        const tableRows = issued_units.map((issued_unit, index) => `
          <tr class="border-b text-sm">
            <td class="px-3 py-1">${index + 1}</td>
            <td class="px-3 py-1">${issued_unit.product?.pint_serial || ''}</td>
            <td class="px-3 py-1">${issued_unit.product?.blood_group?.description || ''}</td>
            <td class="px-3 py-1">${issued_unit.product?.unit_product?.name || ''}</td>
            <td class="px-3 py-1">${issued_unit.requisition_product?.patient_requisition?.patient?.first_name || ''} ${issued_unit.requisition_product?.patient_requisition?.patient?.last_name || ''}</td>
            <td class="px-3 py-1">${issued_unit.requisition_product?.patient_requisition?.patient?.ipop || ''}</td>
            <td class="px-3 py-1">${issued_unit.requisition_product?.patient_requisition?.patient?.blood_group?.description || ''}</td>
            <td class="px-3 py-1">${issued_unit.requisition_product?.patient_requisition?.requisition?.department?.name || ''}</td>
            <td class="px-3 py-1">${this.formatDate(issued_unit.requisition_product?.patient_requisition?.requisition?.date_added) || 'N/A'}</td>
            <td class="px-3 py-1">${this.formatDate(issued_unit.issued_on) || 'N/A'}</td>
            <td class="px-3 py-1 text-center">
              <span>
                ${issued_unit.product?.status === 'Issued' ? 'Received' : issued_unit.product?.status || ''}
              </span>
            </td>
          </tr>
        `).join('');

        const printContent = `
          <div class="page-container">
            <div class="report-container">
              <div class="logo-container">
                <img src="${this.facility_logo}" alt="Hospital Logo" class="logo">
              </div>
              <br><br>
              <h1 class="text-lg font-bold mb-4">Issued Units Report</h1>
              <p class="mb-4">Generated on: ${new Date().toLocaleString()}</p>
              <div class="table-container">
                <table class="w-full text-left">
                  <thead>
                    <tr class="bg-gray-100">
                      <th class="px-6 py-2"></th>
                      <th class="px-6 py-2">Serial no.</th>
                      <th class="px-6">Blood Group</th>
                      <th class="px-6">Blood Product</th>
                      <th class="px-6">Patient Name</th>
                      <th class="px-6">IP/OP Number</th>
                      <th class="px-6">Patient Blood Group</th>
                      <th class="px-6">Ward</th>
                      <th class="px-6">Requisition Date</th>
                      <th class="px-6">Received Date</th>
                      <th class="px-6 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${tableRows}
                  </tbody>
                </table>
              </div>
              <div class="signatures mt-8">
                <div class="signature-line">
                  <p>Prepared by: _________________</p>
                  <p>Date: _________________</p>
                </div>
                <div class="signature-line mt-4">
                  <p>Verified by: _________________</p>
                  <p>Date: _________________</p>
                </div>
              </div>
            </div>
          </div>
        `;

        const printDocument = `
          <html>
          <head>
            <title>Issued Units Report</title>
            <style>
              @media print {
                @page {
                  size: A4;
                  margin: 2cm;
                }
              }
              body {
                font-family: Arial, sans-serif;
                line-height: 1.6;
                color: #333;
                margin: 0;
                padding: 0;
              }
              .logo {
                width: 120px;
                float: right;
              }
              .page-container {
                padding: 20px;
              }
              .report-container {
                max-width: 100%;
                margin: 0 auto;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                margin-bottom: 20px;
                font-size: 10.5px;
              }
              th, td {
                border: 1px solid #ddd;
                padding: 8px;
                text-align: left;
              }
              th {
                background-color: #F3F4F6;
                font-weight: bold;
              }
              .signatures {
                margin-top: 40px;
                page-break-inside: avoid;
                font-size: 10.5px;
              }
              .signature-line {
                margin-bottom: 20px;
              }
              @media print {
                .status-badge {
                  border: 1px solid #ddd;
                }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `;

        printWindow.contentDocument.open();
        printWindow.contentDocument.write(printDocument);
        printWindow.contentDocument.close();

        printWindow.contentWindow.onload = function () {
          printWindow.contentWindow.print();
          setTimeout(() => {
            document.body.removeChild(printWindow);
          }, 1000);
        };
      } catch (error) {
        console.error('Error generating report:', error);
      }
    }
  }
};
</script>

<style lang="css" scoped></style>